import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardAction, CardFooter } from '../ui/card';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { ArrowRight, GitBranch, Users, Layers, Loader2 } from 'lucide-react';
import { ResponsiveSankey } from '@nivo/sankey';

interface MultiChannelAttributionProps {
  startDate: string;
  endDate: string;
}

const MultiChannelAttribution: React.FC<MultiChannelAttributionProps> = ({ startDate, endDate }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    setTimeout(() => {
      setData({
        cross_channel_journeys: 342,
        avg_touchpoints: 2.8,
        sankeyData: {
          nodes: [
            { id: 'Email' }, { id: 'Chat' }, { id: 'Phone' }, { id: 'Social' },
            { id: 'Resolved' }, { id: 'Escalated' }
          ],
          links: [
            { source: 'Email', target: 'Chat', value: 120 },
            { source: 'Email', target: 'Resolved', value: 80 },
            { source: 'Chat', target: 'Phone', value: 45 },
            { source: 'Chat', target: 'Resolved', value: 150 },
            { source: 'Phone', target: 'Resolved', value: 90 },
            { source: 'Social', target: 'Chat', value: 60 }
          ]
        }
      });
      setLoading(false);
    }, 500);
  }, [startDate, endDate]);

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data) {
    return (
      <Alert>
        <AlertDescription>No multi-channel data available.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader>
            <CardDescription>Cross-Channel Journeys</CardDescription>
            <CardAction>
              <div className="rounded-full bg-muted p-2">
                <GitBranch className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardAction>
            <CardTitle className="text-2xl tabular-nums font-semibold">
              {data.cross_channel_journeys.toLocaleString()}
            </CardTitle>
          </CardHeader>
          <CardFooter>
            <span className="text-xs text-muted-foreground">Customers using multiple channels</span>
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader>
            <CardDescription>Avg Touchpoints</CardDescription>
            <CardAction>
              <div className="rounded-full bg-muted p-2">
                <Layers className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardAction>
            <CardTitle className="text-2xl tabular-nums font-semibold">
              {data.avg_touchpoints}
            </CardTitle>
          </CardHeader>
          <CardFooter>
            <span className="text-xs text-muted-foreground">Channels per customer</span>
          </CardFooter>
        </Card>

        <Card className="bg-gradient-to-t from-primary/5 to-card shadow-sm">
          <CardHeader>
            <CardDescription>Most Common Path</CardDescription>
            <CardAction>
              <div className="rounded-full bg-muted p-2">
                <Users className="h-4 w-4 text-muted-foreground" />
              </div>
            </CardAction>
            <CardTitle className="flex items-center gap-1.5 pt-1">
              <Badge variant="secondary" className="text-sm font-semibold">Email</Badge>
              <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
              <Badge variant="secondary" className="text-sm font-semibold">Chat</Badge>
            </CardTitle>
          </CardHeader>
          <CardFooter>
            <span className="text-xs text-muted-foreground">Top customer journey path</span>
          </CardFooter>
        </Card>
      </div>

      {/* Sankey Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm font-medium">Customer Journey Flow (Sankey Diagram)</CardTitle>
          <CardDescription>Visualizing how customers move between channels to reach resolution</CardDescription>
        </CardHeader>
        <CardContent>
          <div style={{ height: 400 }}>
            <ResponsiveSankey
              data={data.sankeyData}
              margin={{ top: 40, right: 160, bottom: 40, left: 50 }}
              align="justify"
              colors={{ scheme: 'category10' }}
              nodeOpacity={1}
              nodeThickness={18}
              nodeInnerPadding={3}
              nodeBorderWidth={0}
              nodeBorderRadius={3}
              linkOpacity={0.5}
              linkHoverOthersOpacity={0.1}
              enableLinkGradient={true}
            />
          </div>
        </CardContent>
      </Card>

      {/* Insights */}
      <Card className="bg-muted/30">
        <CardHeader>
          <CardTitle className="text-sm font-medium">Multi-Channel Insights</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground">
            Tracking customer movement across channels helps optimize resource allocation and identify preferred communication paths.
            Focus on reducing unnecessary channel hops by improving first-contact resolution rates.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default MultiChannelAttribution;
